"use client";

import Link from "next/link";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { ChevronDown, Crown, KeyRound, LogOut, UserRound, type LucideIcon } from "lucide-react";
import { logoutAction } from "@/app/[lang]/(auth)/login/actions";
import { cn } from "@/components/ui/cn";
import { langPath, type Lang } from "@/lib/i18n";
import { useDict } from "@/components/i18n/DictProvider";

/**
 * The account menu — the ONE place account-scoped destinations live
 * (2026-08-25). The sidebar used to carry an "ACCOUNT" section holding the same
 * profile and subscription links; the rail is now pure navigation and this is
 * the account surface, which is the Stripe / Shopify / GitHub arrangement.
 *
 * Styled as a quiet avatar + name, not the gold pill it was. In all three of
 * those products the account control is the most RECESSIVE thing in the bar —
 * you go there to leave, not to work — and gold on this surface is reserved for
 * actions (the dashboard's Ask and Continue buttons).
 */
export function AccountMenu({
  lang,
  name,
  email,
  isSubscription = false,
}: {
  lang: Lang;
  name: string;
  /** Shown as the "signed in as" line — the button truncates long names. */
  email?: string;
  /** Portal `paymentModel` — single/free portals have no subscriptions page. */
  isSubscription?: boolean;
}) {
  const t = useDict();
  const initial = name.trim().charAt(0).toUpperCase() || "?";

  const links: { href: string; label: string; Icon: LucideIcon }[] = [
    { href: "/profil", label: t.nav.profile, Icon: UserRound },
    ...(isSubscription
      ? [{ href: "/profil/narocnine", label: t.profile.subscriptions, Icon: Crown }]
      : []),
    { href: "/profil/geslo", label: t.profile.security, Icon: KeyRound },
  ];

  return (
    <Menu as="div" className="relative">
      <MenuButton className="font-body inline-flex h-10 items-center gap-2 rounded-pill pl-1 pr-2.5 text-foreground transition-colors hover:bg-foreground/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 data-[active]:bg-foreground/[0.06]">
        <span
          aria-hidden
          className="grid size-8 shrink-0 place-items-center rounded-full bg-primary/12 font-display text-[13px] font-extrabold text-primary"
        >
          {initial}
        </span>
        <span className="hidden max-w-[150px] truncate text-[14px] font-semibold sm:block">
          {name}
        </span>
        <ChevronDown className="size-3.5 shrink-0 text-foreground/40" aria-hidden />
      </MenuButton>

      <Transition
        enter="transition duration-150 ease-out"
        enterFrom="opacity-0 -translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition duration-100 ease-in"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-1"
      >
        <MenuItems
          className="absolute right-0 z-30 mt-2 w-60 origin-top-right overflow-hidden rounded-2xl border border-border bg-card text-card-foreground focus:outline-none"
          style={{ boxShadow: "var(--shadow-lift)" }}
        >
          <div className="border-b border-border px-4 py-3">
            <p className="truncate font-display text-[13px] font-bold text-foreground">{name}</p>
            {email ? (
              <p className="mt-0.5 truncate text-[12px] text-muted-foreground">{email}</p>
            ) : null}
          </div>

          {links.map(({ href, label, Icon }) => (
            <MenuItem key={href}>
              {({ focus }) => (
                <Link
                  href={langPath(lang, href)}
                  className={cn(
                    "flex items-center gap-2.5 px-4 py-2.5 text-sm font-bold",
                    focus ? "bg-muted text-foreground" : "text-foreground/85",
                  )}
                >
                  <Icon className="size-4 shrink-0 text-foreground/45" aria-hidden />
                  {label}
                </Link>
              )}
            </MenuItem>
          ))}

          <div className="border-t border-border" />
          <MenuItem>
            {({ focus }) => (
              <form action={logoutAction}>
                <input type="hidden" name="lang" value={lang} />
                <button
                  type="submit"
                  className={cn(
                    "flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-bold",
                    focus ? "bg-muted text-foreground" : "text-foreground/85",
                  )}
                >
                  <LogOut className="size-4 shrink-0 text-foreground/45" aria-hidden />
                  {t.nav.logout}
                </button>
              </form>
            )}
          </MenuItem>
        </MenuItems>
      </Transition>
    </Menu>
  );
}
